import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import { Link } from 'core/routing';
import { imageSrc } from 'apps/file/utils';
import { conf as mainConf } from 'apps/config.yaml';
import { modalStore } from 'apps/modals/app';
import { ModalExpired } from 'apps/modals/freeTrial/expired';
import { clientStore } from 'apps/client/app';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { Button, ConfirmModal, OverflowTooltip, IconButton } from 'components';
import Icon from 'components/Icon';
import { CardImage } from 'legacyComponents/cards/common';
import Tooltip from 'components/Tooltip';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';
import { themes } from 'styles/themes/wikifactoryNew';
import {
  Card,
  TagsWrapper,
  CardTag,
  Title,
  CardFooter,
  CardFooterList,
  CardFooterItem,
  CardFooterButton,
  TypeTag,
} from 'legacyComponents/cards/styles';
import { Div } from 'legacyComponents/styled/lib';
import { FileSync, WorkspaceSettings } from 'icons';

import { SpaceLink } from 'apps/shared';
import { getSystemCondition } from 'apps/routes/utils';

@withNamespaces()
@observer
export class ProjectCard extends React.Component {
  state = {
    hoverId: '',
    openDeleteModal: false,
  };

  showTrialModal = e => {
    e.preventDefault();
    const project = this.props.cardObject;
    const { Subscribe } =
      project.manager.manager.manager || project.manager.manager;
    modalStore.SetModalComponent(ModalExpired, {
      spaceType: project.space.spaceType,
      clickPurchaseButton: () => Subscribe({ spaceSlug: project.spacePath }),
    });
  };

  handleClickDeleteIcon = () => {
    this.setState({
      openDeleteModal: true,
    });
  };

  closeDeleteModal = () => {
    this.setState({
      openDeleteModal: false,
    });
  };

  render() {
    const { cardObject, cardNo, cardColumns, t } = this.props;
    const { hoverId, openDeleteModal } = this.state;

    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='project' />;
    }

    const {
      image,
      descriptionSnippet,
      contributionCount,
      slug,
      spacePath,
      tags,
      space,
      content,
      name,
      id,
      canUpdate,
      canDelete,
      private: isPrivate,
      contributors,
    } = cardObject;

    const { totalCount: contributorTotal = 0 } = contributors || {};
    const { followersCount } = content;
    const spaceType = spacePath[0] === '+' ? 'initiative' : 'profile';
    const route = isInDesktopMode()
      ? `${spaceType}.project.files`
      : `${spaceType}.project`;

    const hasPermission = canUpdate || canDelete;
    const showHoverState = hoverId === id && hasPermission;
    const footerStyle = showHoverState
      ? { background: themes.color.wfGrey100 }
      : {};

    const projectSettingRouteName = `${spaceType}.project.settings.teams`;

    const tagsList = tags.map((tag, index) => (
      <Link
        name='channel'
        params={{ channel: tag.name }}
        key={tag?.id ? `${tag.id}_${index}` : index}
        content={{ main: true }}
      >
        <CardTag vertical>#{tag.name}</CardTag>
      </Link>
    ));

    const { trialPeriodEnd } =
      this.props.cardObject && this.props.cardObject.space;

    const fileSyncTag = this.props.cardObject.fileSyncConnected ? (
      <div className='flex absolute left-3 top-4 z-50'>
        <div className='border-2 rounded-3xl border-green-300 p-1 bg-white bg-opacity-90'>
          <FileSync stroke='black' />
        </div>
        <div className='flex ml-1 bg-white bg-opacity-90 justify-between items-center border-2 rounded-3xl border-green-300 px-2 py-1 text-black'>
          <div>{t('Synced')}</div>
          <div className='ml-2 w-3 h-3 rounded-full bg-green-300' />
        </div>
      </div>
    ) : null;

    const { showPublicOrPrivateForProjectTag } = getSystemCondition();

    return (
      <Card
        noMargin={noMargin}
        hasTags
        data-intercom-target={cardNo === 0 ? 'organisation-projects-list' : ''}
        onMouseEnter={() => {
          this.setState({
            hoverId: cardObject.id,
          });
        }}
        onMouseLeave={() => {
          this.setState({
            hoverId: '',
          });
        }}
      >
        <Div position='relative'>
          <Link
            name={route}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
            openTab={{
              title: name,
              iconType: 'project',
            }}
          >
            {fileSyncTag}
            <Tooltip id='tooltipBanner' />
            {showPublicOrPrivateForProjectTag && cardObject.private && (
              <TypeTag>
                <Icon name='locked' />
                {t('Private')}
              </TypeTag>
            )}
            {showPublicOrPrivateForProjectTag && !cardObject.private && (
              <TypeTag isPublic>
                <Icon name='website' />
                {t('Public')}
              </TypeTag>
            )}
            <CardImage
              src={imageSrc(
                image,
                cardObject.getImageFallbackPath(
                  showPublicOrPrivateForProjectTag && cardObject.private,
                ),
                mainConf.imgSizes.project.card,
              )}
              alt={name}
            />
          </Link>
          <TagsWrapper withPrivate={cardObject.private && trialPeriodEnd}>
            {tagsList}
          </TagsWrapper>
        </Div>
        <div className='flex flex-col m-4 h-full w-4/5'>
          <Link
            name={route}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
            openTab={{
              title: name,
              iconType: 'project',
            }}
          >
            <Title
              data-testid={`project-${name}`}
              style={{
                marginBottom: 0,
                whiteSpace: 'break-spaces',
                wordBreak: 'break-all',
              }}
            >
              <OverflowTooltip text={name} />
            </Title>
          </Link>
          <SpaceLink
            label='Created by'
            space={space}
            underlined={false}
            variant='primary'
            preferWorkspaceLink
          />
          <p className='text-black'>{descriptionSnippet}</p>
        </div>
        <CardFooter data-testid={`ProjectPage-${slug}LinkWrapper`}>
          <Tooltip />
          <CardFooterList style={footerStyle}>
            {/*! cardObject.private && (
              <CardFooterItem>
                <Icon name='view' data-tip={t('Views')} />
                {pageViewsFormatted}
              </CardFooterItem>
            ) */}
            {showHoverState ? (
              <div className='flex w-full ml-8'>
                {canDelete && (
                  <Button
                    outline
                    size='small'
                    icon='delete'
                    variant='destructive'
                    onClick={() => {
                      this.handleClickDeleteIcon();
                    }}
                  />
                )}
                {canUpdate && (
                  <Link
                    name={projectSettingRouteName}
                    params={{ [spaceType]: spacePath, slug }}
                    dataTestId={`ProjectPage-${slug}Link`}
                    content={content}
                    openTab={{
                      title: name,
                      iconType: 'project',
                    }}
                  >
                    <IconButton
                      variant='outlined'
                      icon={<WorkspaceSettings viewBox='3 2 18 18' />}
                      style={{
                        marginLeft: 8,
                        color: '#000',
                        borderColor: '#000',
                      }}
                    />
                  </Link>
                )}
              </div>
            ) : (
              <>
                <CardFooterItem>
                  <Icon
                    name='user'
                    data-tip={isPrivate ? t('Contributors') : t('Followers')}
                  />
                  {isPrivate ? contributorTotal : followersCount}
                </CardFooterItem>
                <CardFooterItem>
                  <Icon name='contribution' data-tip={t('Contributions')} />
                  {contributionCount}
                </CardFooterItem>
              </>
            )}
          </CardFooterList>
          <Link
            name={route}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
            openTab={{
              title: name,
              iconType: 'project',
            }}
          >
            <CardFooterButton
              style={
                showHoverState
                  ? {
                      ...footerStyle,
                      height: '100%',
                      color: themes.color.wfBlue,
                      borderColor: themes.color.wfGrey300,
                    }
                  : {}
              }
              icon='arrow_right'
              size='small'
              appearance='plain'
            />
          </Link>
        </CardFooter>
        <ConfirmModal
          open={openDeleteModal}
          loading={cardObject.deleting}
          title='Are you sure you want to delete this Project?'
          onConfirm={() => {
            cardObject.ConfirmDeleteByProjectId(id, () => {
              const projectsConn = cardObject.manager;
              projectsConn.Reset();
              projectsConn.loadMore();

              clientStore.UpdateProfileOrTeamWorkspace();
              this.closeDeleteModal();
            });
          }}
          onClose={() => {
            this.closeDeleteModal();
          }}
        />
      </Card>
    );
  }
}

ProjectCard.propTypes = {
  cardObject: PropTypes.object,
  cardColumns: PropTypes.number,
  cardNo: PropTypes.number,
  t: PropTypes.func,
};
